import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@iconify/react';
import AdminDashboard from './AdminDashboard';
import UserSelector from './UserSelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || '';

interface User {
  id: string;
  auth_id: string;
  email: string;
  name: string;
  phone?: string;
  isAdmin: boolean;
  status: string;
}

interface Cart {
  id: string;
  cart_number: number;
  qr_code: string;
  status: string;
  seats?: number;
  color?: string;
  description?: string;
  out_of_service?: boolean;
  cart_images?: string[];
  current_checkout?: {
    id: string;
    user_id: string;
    checked_out_at: string;
    users: {
      id: string;
      name: string;
      email: string;
      phone?: string;
    };
  } | null;
}

function App() {
  // Auth0 hooks
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently
  } = useAuth0();

  // App user profile (from our Supabase users table)
  const [user, setUser] = useState<User | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // My Profile modal state
  const [showProfile, setShowProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [profileNewPassword, setProfileNewPassword] = useState('');
  const [profileConfirmPassword, setProfileConfirmPassword] = useState('');

  // App state
  const [carts, setCarts] = useState<Cart[]>([]);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [transferCartId, setTransferCartId] = useState<string | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [config, setConfig] = useState<{logo?: string; tagline?: string}>({});

  // Cart images modal state
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState<Cart | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // ── Helper: get a fresh Auth0 access token ──────────────────────────────────
  const getToken = async (): Promise<string> => {
    return await getAccessTokenSilently({
      authorizationParams: { audience: AUTH0_AUDIENCE }
    });
  };

  // ── On auth state change: sync user profile ─────────────────────────────────
  useEffect(() => {
    if (isAuthenticated) {
      syncUserProfile();
    } else if (!auth0Loading) {
      setUser(null);
      setCarts([]);
    }
  }, [isAuthenticated, auth0Loading]);

  // ── Fetch carts once user profile is loaded ──────────────────────────────────
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCarts();
    }
  }, [isAuthenticated, user]);

  // ── Check for /welcome redirect after invitation acceptance ─────────────────
  useEffect(() => {
    if (window.location.pathname === '/welcome' && isAuthenticated) {
      console.log('Welcome redirect detected — marking invitation accepted');
      window.history.replaceState(null, '', '/');
      acceptInvitation();
    }
    fetchConfig();
  }, []);

  // ── Camera scanner cleanup ───────────────────────────────────────────────────
  useEffect(() => {
    let html5QrCode: any = null;

    const initScanner = async () => {
      if (showCameraScanner && typeof window !== 'undefined') {
        try {
          const { Html5Qrcode } = await import('html5-qrcode');
          html5QrCode = new Html5Qrcode('qr-reader');
          const cfg = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 };
          await html5QrCode.start(
            { facingMode: 'environment' },
            cfg,
            (decodedText: string) => {
              console.log('QR Code scanned:', decodedText);
              setQrInput(decodedText);
              setShowCameraScanner(false);
              handleQRScan(new Event('submit') as any, decodedText);
            },
            (_errorMessage: string) => { /* ignore per-frame scan errors */ }
          );
        } catch (error) {
          console.error('Scanner initialization error:', error);
          alert('Failed to start camera. Please check camera permissions and try again.');
          setShowCameraScanner(false);
        }
      }
    };

    initScanner();

    return () => {
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().catch((err: any) => {
          console.error('Error stopping scanner:', err);
        });
      }
    };
  }, [showCameraScanner]);

  // ── API helpers ──────────────────────────────────────────────────────────────

  const syncUserProfile = async () => {
    try {
      setProfileLoading(true);
      const token = await getToken();
      console.log('Syncing user profile...');
      const response = await fetch(`${API_URL}/api/auth/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('User data received:', userData);
        const mapped: User = { ...userData, id: userData.id, isAdmin: userData.is_admin };
        setUser(mapped);

        // If user is pending (hasn't completed first-time setup), open profile modal
        if (userData.status === 'pending') {
          console.log('User is pending - opening password setup modal');
          setTimeout(() => openProfileModal(mapped), 100);
        }
      } else if (response.status === 404) {
        // Authenticated with a social provider but no account exists for this email.
        // This means they are not an invited user.
        console.warn('No user profile found for this account — not an invited user');
        alert('No account found for this sign-in. Access is by invitation only. Please contact an administrator.');
        logout({ logoutParams: { returnTo: window.location.origin } });
      } else {
        console.error('Failed to sync user profile:', response.status);
      }
    } catch (error) {
      console.error('Failed to sync user profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const acceptInvitation = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/accept-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        console.log('Invitation accepted successfully');
        await syncUserProfile();
      } else {
        const err = await response.json();
        console.warn('Accept invitation response:', err.error);
      }
    } catch (error) {
      console.error('Accept invitation error:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchCarts = async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/carts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCarts(await response.json());
      }
    } catch (error) {
      console.error('Error fetching carts:', error);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setCarts([]);
    setShowProfile(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const openProfileModal = (userData?: User) => {
    const u = userData || user;
    if (u) {
      setProfileName(u.name || '');
      setProfileEmail(u.email || '');
      setProfilePhone(u.phone || '');
      setCurrentPassword('');
      setProfileNewPassword('');
      setProfileConfirmPassword('');
      setShowProfile(true);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    const isFirstPasswordSetup = user?.status === 'pending';

    if (profileNewPassword || profileConfirmPassword) {
      if (!isFirstPasswordSetup && !currentPassword) {
        alert('Please enter your current password to change your password');
        return;
      }
      if (profileNewPassword !== profileConfirmPassword) {
        alert('New passwords do not match');
        return;
      }
      if (profileNewPassword.length < 8) {
        alert('New password must be at least 8 characters');
        return;
      }
    }

    if (isFirstPasswordSetup && !profileNewPassword) {
      alert('You must set a password to activate your account');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profileName,
          email: profileEmail,
          phone: profilePhone,
          currentPassword: currentPassword || undefined,
          newPassword: profileNewPassword || undefined,
          isFirstPasswordSetup: isFirstPasswordSetup
        })
      });

      if (response.ok) {
        const result = await response.json();

        if (isFirstPasswordSetup) {
          console.log('First-time password setup completed');
          if (!result.accountActivated) {
            alert('Password was set but account activation failed. Please contact support.');
            return;
          }
          alert('✅ Password set successfully!\n\nYour account has been activated. You will now be logged out.\n\nPlease log in again using your email address and the password you just set.');
          setShowProfile(false);
          handleLogout();
        } else {
          alert('Profile updated successfully!');
          setShowProfile(false);
          await syncUserProfile();
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update profile');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to update profile');
    }
  };

  const handleCheckout = async (cartId: string) => {
    if (!isAuthenticated) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cart_id: cartId })
      });
      if (response.ok) {
        alert('Cart checked out successfully!');
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Checkout failed');
      }
    } catch (error) {
      alert('Checkout failed');
    }
  };

  const handleCheckin = async (cartId: string) => {
    if (!isAuthenticated) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/checkin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cart_id: cartId })
      });
      if (response.ok) {
        alert('Cart checked in successfully!');
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Check-in failed');
      }
    } catch (error) {
      alert('Check-in failed');
    }
  };

  const handleTransferSelect = async (toUserId: string) => {
    if (!isAuthenticated || !transferCartId) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cart_id: transferCartId, to_user_id: toUserId })
      });
      if (response.ok) {
        alert('Cart transferred successfully!');
        setTransferCartId(null);
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'Transfer failed');
      }
    } catch (error) {
      alert('Transfer failed');
    }
  };

  const handleQRScan = async (e: React.FormEvent, scannedCode?: string) => {
    e.preventDefault();
    const codeToProcess = scannedCode || qrInput.trim();
    if (!isAuthenticated || !codeToProcess) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/scan-qr`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ qr_code: codeToProcess })
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Cart #${data.cart.cart_number} ${data.action === 'checkout' ? 'checked out' : 'checked in'} successfully!`);
        setQrInput('');
        setShowQRScanner(false);
        setShowCameraScanner(false);
        fetchCarts();
      } else {
        const error = await response.json();
        alert(error.error || 'QR scan failed');
      }
    } catch (error) {
      alert('QR scan failed');
    }
  };

  // ── Render: Auth0 loading ────────────────────────────────────────────────────
  if (auth0Loading || (isAuthenticated && profileLoading && !user)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl text-gray-700">Loading...</div>
      </div>
    );
  }

  // ── Render: Login page ───────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo and Tagline */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img
                src={config.logo || '/favicon.png'}
                alt="Logo"
                className="w-[150px] h-[150px] object-contain rounded-2xl shadow-lg bg-white p-4"
                onError={(e) => { e.currentTarget.src = '/favicon.png'; }}
              />
            </div>
            {config.tagline && (
              <p className="text-xl font-semibold text-gray-700 italic">
                {config.tagline}
              </p>
            )}
          </div>

          {/* Login Box */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center gap-2">
              <img src="/favicon.png" alt="Golf Cart" className="w-8 h-8" />
              Golf Cart Manager
            </h1>

            <button
              onClick={() => loginWithRedirect()}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2"
            >
              <Icon icon="mdi:login" width="20" />
              Sign In
            </button>

            <p className="text-center text-sm text-gray-500 mt-4">
              Use the "Forgot password?" link on the sign-in page to reset your password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Profile sync still in progress ───────────────────────────────────
  if (isAuthenticated && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl text-gray-700">Loading your profile...</div>
      </div>
    );
  }

  // ── Render: Pending user — must complete first-time setup ────────────────────
  if (user && user.status === 'pending' && !showProfile) {
    console.log('Blocking dashboard for pending user:', user.name);
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          {config.logo && <img src={config.logo} alt="Logo" className="h-16 mx-auto mb-4" />}
          <div className="mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:lock-alert" width="32" className="text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome, {user.name}!</h2>
            <p className="text-gray-600 mb-4">
              Before you can access Golf Cart Manager, you must set your password.
            </p>
            <p className="text-sm text-gray-500">
              Click the button below to set your password. After setting it, you'll be logged out and can sign in with your new credentials.
            </p>
          </div>
          <button
            onClick={() => openProfileModal()}
            className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition mb-3"
          >
            Set Password Now
          </button>
          <button
            onClick={handleLogout}
            className="w-full text-gray-600 font-medium hover:underline"
          >
            Log Out
          </button>
        </div>
        {showProfile && renderProfileModal()}
      </div>
    );
  }

  // ── Render: Admin dashboard ──────────────────────────────────────────────────
  if (showAdmin && user?.isAdmin) {
    return (
      <AdminDashboard
        user={user}
        getToken={getToken}
        onBack={() => {
          setShowAdmin(false);
          fetchCarts();
        }}
      />
    );
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const renderSeatIcons = (seats: number) => (
    <div className="flex items-center gap-1">
      {Array.from({ length: seats }).map((_, index) => (
        <Icon key={index} icon="mdi:account" width="16" className="text-gray-600" />
      ))}
    </div>
  );

  function renderProfileModal() {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {user?.status === 'pending' ? 'Set Your Password' : 'My Profile'}
            </h2>
            {user?.status !== 'pending' && (
              <button onClick={() => setShowProfile(false)} className="text-gray-500 hover:text-gray-700">
                <Icon icon="mdi:close" width="24" />
              </button>
            )}
          </div>

          <form onSubmit={handleProfileUpdate} className="space-y-4">
            {/* Personal Information — only for established accounts */}
            {user?.status !== 'pending' && (
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Personal Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={profileEmail}
                      onChange={(e) => setProfileEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Changing your email will require verification</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={profilePhone}
                      onChange={(e) => setProfilePhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Password section */}
            <div className="border-b pb-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">
                {user?.status === 'pending' ? 'Set Your Password' : 'Change Password'}
              </h3>
              {user?.status === 'pending' ? (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-3 mb-3">
                  <p className="text-sm text-orange-800 font-medium">
                    ⚠️ You must set a password to activate your account and access the system.
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    After setting your password, you'll be logged out and will need to sign in again.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 mb-3">Leave blank if you don't want to change your password</p>
              )}

              <div className="space-y-3">
                {user?.status !== 'pending' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Required to change password"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {user?.status === 'pending' ? 'Password' : 'New Password'}
                    {user?.status === 'pending' && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="password"
                    value={profileNewPassword}
                    onChange={(e) => setProfileNewPassword(e.target.value)}
                    minLength={8}
                    required={user?.status === 'pending'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Min 8 characters"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm {user?.status === 'pending' ? 'Password' : 'New Password'}
                    {user?.status === 'pending' && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="password"
                    value={profileConfirmPassword}
                    onChange={(e) => setProfileConfirmPassword(e.target.value)}
                    minLength={8}
                    required={user?.status === 'pending'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm password"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {user?.status !== 'pending' && (
                <button
                  type="button"
                  onClick={() => setShowProfile(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className={`${
                  user?.status === 'pending'
                    ? 'w-full bg-orange-600 hover:bg-orange-700'
                    : 'flex-1 bg-blue-600 hover:bg-blue-700'
                } px-4 py-2 text-white rounded-lg transition font-medium`}
              >
                {user?.status === 'pending' ? 'Set Password & Activate Account' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: Main dashboard ───────────────────────────────────────────────────
  console.log('Rendering main dashboard. User:', user?.name, 'Status:', user?.status);
  const myCheckouts = carts.filter(cart => cart.current_checkout?.user_id === user?.id);
  const availableCarts = carts.filter(cart => !cart.current_checkout);
  const othersCheckouts = carts.filter(cart => cart.current_checkout && cart.current_checkout.user_id !== user?.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                <img src="/favicon.png" alt="Golf Cart" className="w-8 h-8" />
                Golf Cart Manager
              </h1>
              <p className="text-gray-600 mt-1">Welcome, {user?.name}!</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setShowQRScanner(!showQRScanner)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
              >
                <Icon icon="mdi:qrcode-scan" width="20" />
                {showQRScanner ? 'Hide Scanner' : 'Scan QR'}
              </button>
              {user?.isAdmin && (
                <button
                  onClick={() => setShowAdmin(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium flex items-center gap-2"
                >
                  <Icon icon="mdi:shield-crown" width="20" />
                  Admin
                </button>
              )}
              <button
                onClick={() => openProfileModal()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
              >
                <Icon icon="mdi:account-circle" width="20" />
                My Profile
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium flex items-center gap-2"
              >
                <Icon icon="mdi:logout" width="20" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* QR Scanner */}
        {showQRScanner && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">QR Code Scanner</h2>
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowCameraScanner(!showCameraScanner)}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                <Icon icon={showCameraScanner ? 'mdi:camera-off' : 'mdi:camera'} width="24" />
                {showCameraScanner ? 'Close Camera' : 'Open Camera Scanner'}
              </button>
            </div>
            {showCameraScanner && (
              <div className="mb-4">
                <div className="relative bg-black rounded-lg overflow-hidden" style={{ minHeight: '300px' }}>
                  <div id="qr-reader" className="w-full"></div>
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4">
                    <p className="text-white text-center text-sm">
                      📸 Position the QR code within the frame
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  The camera will automatically scan QR codes when detected
                </p>
              </div>
            )}
            <form onSubmit={handleQRScan} className="flex gap-2">
              <input
                type="text"
                placeholder="Or manually enter cart number"
                value={qrInput}
                onChange={(e) => setQrInput(e.target.value)}
                className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button type="submit" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                Submit
              </button>
            </form>
          </div>
        )}

        {/* My Checkouts */}
        {myCheckouts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">My Checked Out Carts ({myCheckouts.length})</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {myCheckouts.map(cart => {
                const checkoutDate = new Date(cart.current_checkout!.checked_out_at);
                const dateStr = checkoutDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
                const timeStr = checkoutDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return (
                  <div key={cart.id} className="border-2 border-green-300 rounded-xl p-4 bg-green-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-4xl font-bold text-green-700">#{cart.cart_number}</div>
                      {cart.color && <div className="w-6 h-6 rounded-full border-2 border-gray-300" style={{ backgroundColor: cart.color.toLowerCase() }} />}
                    </div>
                    {cart.seats && (
                      <div className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                        {renderSeatIcons(cart.seats)}
                        <span>{cart.seats} {cart.seats === 1 ? 'seat' : 'seats'}</span>
                      </div>
                    )}
                    {cart.description && <div className="text-xs text-gray-500 mb-2 italic">{cart.description}</div>}
                    <div className="text-sm text-gray-600 mb-1">{cart.current_checkout?.users?.name || user?.name}</div>
                    {cart.current_checkout?.users?.phone && (
                      <div className="text-sm text-gray-600 mb-2">
                        <Icon icon="mdi:phone" className="inline mr-1" width="14" />
                        {cart.current_checkout.users.phone}
                      </div>
                    )}
                    <div className="text-sm text-gray-600 mb-3">Since {timeStr} on {dateStr}</div>
                    {cart.cart_images && cart.cart_images.length > 0 && (
                      <button
                        onClick={() => { setSelectedCart(cart); setCurrentImageIndex(0); setShowImagesModal(true); }}
                        className="w-full py-1 mb-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm flex items-center justify-center gap-1"
                      >
                        <Icon icon="mdi:image-multiple" width="16" />
                        View Images ({cart.cart_images.length})
                      </button>
                    )}
                    <div className="space-y-2">
                      <button onClick={() => handleCheckin(cart.id)} className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">Check In</button>
                      <button onClick={() => setTransferCartId(cart.id)} className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">Transfer</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Transfer Modal */}
        {transferCartId && (
          <UserSelector
            getToken={getToken}
            apiUrl={API_URL}
            onSelect={handleTransferSelect}
            onCancel={() => setTransferCartId(null)}
            currentUserId={user?.id}
          />
        )}

        {/* Available Carts */}
        {availableCarts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Available Carts ({availableCarts.length})</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {availableCarts.map(cart => {
                const isOutOfService = cart.out_of_service || false;
                return (
                  <div
                    key={cart.id}
                    className={`border-2 rounded-xl p-4 ${isOutOfService ? 'border-gray-300 bg-gray-100 opacity-60' : 'border-gray-300'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-4xl font-bold ${isOutOfService ? 'text-gray-400' : 'text-gray-700'}`}>
                        #{cart.cart_number}
                      </div>
                      {cart.color && (
                        <div
                          className="w-6 h-6 rounded-full border-2 border-gray-300"
                          style={{ backgroundColor: isOutOfService ? '#9ca3af' : cart.color.toLowerCase() }}
                        />
                      )}
                    </div>
                    {cart.seats && (
                      <div className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                        {renderSeatIcons(cart.seats)}
                        <span>{cart.seats} {cart.seats === 1 ? 'seat' : 'seats'}</span>
                      </div>
                    )}
                    {cart.description && (
                      <div className={`text-xs mb-3 italic ${isOutOfService ? 'text-gray-400' : 'text-gray-500'}`}>
                        {cart.description}
                      </div>
                    )}
                    {cart.cart_images && cart.cart_images.length > 0 && (
                      <button
                        onClick={() => { setSelectedCart(cart); setCurrentImageIndex(0); setShowImagesModal(true); }}
                        className="w-full py-1 mb-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm flex items-center justify-center gap-1"
                      >
                        <Icon icon="mdi:image-multiple" width="16" />
                        View Images ({cart.cart_images.length})
                      </button>
                    )}
                    {isOutOfService ? (
                      <div className="w-full py-2 bg-gray-300 text-gray-500 rounded-lg text-center font-medium">
                        Out of Service
                      </div>
                    ) : (
                      <button
                        onClick={() => handleCheckout(cart.id)}
                        className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        Check Out
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Others' Checkouts */}
        {othersCheckouts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Currently Checked Out ({othersCheckouts.length})</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {othersCheckouts.map(cart => {
                const checkoutDate = new Date(cart.current_checkout!.checked_out_at);
                const dateStr = checkoutDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
                const timeStr = checkoutDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return (
                  <div key={cart.id} className="border-2 border-orange-300 rounded-xl p-4 bg-orange-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-4xl font-bold text-orange-700">#{cart.cart_number}</div>
                      {cart.color && <div className="w-6 h-6 rounded-full border-2 border-gray-300" style={{ backgroundColor: cart.color.toLowerCase() }} />}
                    </div>
                    {cart.seats && (
                      <div className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                        {renderSeatIcons(cart.seats)}
                        <span>{cart.seats} {cart.seats === 1 ? 'seat' : 'seats'}</span>
                      </div>
                    )}
                    {cart.description && <div className="text-xs text-gray-500 mb-2 italic">{cart.description}</div>}
                    <div className="text-sm text-gray-700 font-medium mb-1">{cart.current_checkout?.users?.name || 'Unknown User'}</div>
                    {cart.current_checkout?.users?.phone && (
                      <div className="text-sm text-gray-600 mb-1">
                        <Icon icon="mdi:phone" className="inline mr-1" width="14" />
                        {cart.current_checkout.users.phone}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-2">Since {timeStr} on {dateStr}</div>
                    {cart.cart_images && cart.cart_images.length > 0 && (
                      <button
                        onClick={() => { setSelectedCart(cart); setCurrentImageIndex(0); setShowImagesModal(true); }}
                        className="w-full py-1 mt-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm flex items-center justify-center gap-1"
                      >
                        <Icon icon="mdi:image-multiple" width="16" />
                        View Images ({cart.cart_images.length})
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* My Profile Modal */}
        {showProfile && renderProfileModal()}

        {/* Cart Images Modal with Carousel */}
        {showImagesModal && selectedCart && selectedCart.cart_images && selectedCart.cart_images.length > 0 && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowImagesModal(false)}
          >
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Cart #{selectedCart.cart_number} - Images</h3>
                <button onClick={() => setShowImagesModal(false)} className="text-gray-500 hover:text-gray-700">
                  <Icon icon="mdi:close" width="24" />
                </button>
              </div>
              <div className="relative mb-4">
                <div className="flex justify-center items-center bg-gray-100 rounded-lg" style={{ minHeight: '400px' }}>
                  <img
                    src={selectedCart.cart_images[currentImageIndex]}
                    alt={`Cart ${selectedCart.cart_number} - Image ${currentImageIndex + 1}`}
                    className="max-w-full max-h-[500px] h-auto object-contain rounded-lg"
                  />
                </div>
                {selectedCart.cart_images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((currentImageIndex - 1 + selectedCart.cart_images!.length) % selectedCart.cart_images!.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 shadow-lg"
                    >
                      <Icon icon="mdi:chevron-left" width="24" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((currentImageIndex + 1) % selectedCart.cart_images!.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 shadow-lg"
                    >
                      <Icon icon="mdi:chevron-right" width="24" />
                    </button>
                  </>
                )}
              </div>
              {selectedCart.cart_images.length > 1 && (
                <div className="flex gap-2 justify-center mb-4 overflow-x-auto pb-2">
                  {selectedCart.cart_images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${idx === currentImageIndex ? 'border-blue-600' : 'border-gray-300'}`}
                    >
                      <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <div className="text-center text-sm text-gray-600">
                Image {currentImageIndex + 1} of {selectedCart.cart_images.length}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;